import React, { useEffect } from 'react';
import { Globe, Mail, Download, X, ExternalLink } from 'lucide-react';
import { CategoryBadge } from '@portal/components/event_common/CategoryBadge';
import { Sponsor } from '../../types/sponsor.types';
import { Category } from '@portal/components/event_common/types';

interface SponsorMaterial {
  id: string;
  name: string;
  url: string;
  type: 'pdf' | 'video' | 'link' | 'document';
}

interface EnhancedSponsor extends Sponsor {
  specialOffers?: string[];
  materials?: SponsorMaterial[];
  contactEmail?: string;
  boothLocation?: string;
}

interface SponsorDetailModalProps {
  sponsor: EnhancedSponsor;
  isOpen?: boolean;
  onClose: () => void;
  // Optional features that can be enabled/disabled
  canDownloadMaterials?: boolean;
  showCategories?: boolean;
  showBoothLocation?: boolean;
  showSpecialOffers?: boolean;
  showMaterials?: boolean;
  showLastVisited?: boolean;
  lastVisitedAt?: string;
  // Optional callbacks for tracking
  onWebsiteClick?: () => void;
  onViewTracked?: () => void;
}

export const SponsorDetailModal: React.FC<SponsorDetailModalProps> = ({
  sponsor,
  isOpen = true,
  onClose,
  canDownloadMaterials = false,
  showCategories = true,
  showBoothLocation = true,
  showSpecialOffers = true,
  showMaterials = true,
  showLastVisited = false,
  lastVisitedAt,
  onWebsiteClick,
  onViewTracked,
}) => {
  const viewTrackedRef = React.useRef(false);

  // Track view when modal opens (only once)
  useEffect(() => {
    if (isOpen && !viewTrackedRef.current && onViewTracked) {
      viewTrackedRef.current = true;
      onViewTracked();
    }
  }, [isOpen, onViewTracked]);

  // Handle ESC key press
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // For backward compatibility, support both backgroundImageUrl and backgroundImageLargeUrl
  const bannerUrl = sponsor.backgroundImageLargeUrl || sponsor.backgroundImageMediumUrl || sponsor.backgroundImageUrl;
  const logoUrl = sponsor.logoLargeUrl || sponsor.logoMediumUrl || sponsor.logoUrl;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={(e) => {
        // Close when clicking outside the modal content
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-xl">
        {/* Promotional banner image with blurred background */}
        {bannerUrl && (
          <div className="relative w-full overflow-hidden" style={{ aspectRatio: '3 / 1' }}>
            {/* Blurred background image that fills the container */}
            <div className="absolute inset-0">
              <img
                src={bannerUrl}
                alt=""
                className="w-full h-full object-cover blur-xl scale-110 opacity-90"
                aria-hidden="true"
              />
              {/* Slight dark overlay for better contrast */}
              <div className="absolute inset-0 bg-black/5" />
            </div>

            {/* Main image - contained within the white background */}
            <div className="relative h-full w-full flex items-center justify-center">
              <img
                src={bannerUrl}
                alt={`${sponsor.name} banner`}
                className="max-w-full max-h-full object-contain"
                style={{
                  backgroundColor: 'white',
                }}
              />
            </div>
          </div>
        )}

        {/* Modal Header */}
        <div className="bg-white border-b border-gray-200 p-6 flex items-start justify-between">
          <div className="flex items-center gap-4 flex-1">
            {/* Logo in rounded container */}
            {logoUrl && (
              <div className="bg-gray-50 rounded-xl p-3 flex items-center justify-center w-24 h-24">
                <img
                  src={logoUrl}
                  alt={sponsor.name}
                  className="w-full h-full object-contain"
                />
              </div>
            )}
            <div>
              <h2 className="text-2xl font-semibold">{sponsor.heading || sponsor.name}</h2>
              {/* Show name if heading is different */}
              {sponsor.heading && sponsor.heading !== sponsor.name && (
                <p className="text-lg text-gray-600">{sponsor.name}</p>
              )}
              {showBoothLocation && sponsor.boothLocation && (
                <p className="text-gray-600 mt-1">{sponsor.boothLocation}</p>
              )}
              {/* Categories */}
              {showCategories && sponsor.universalCategories && sponsor.universalCategories.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {sponsor.universalCategories.map(category => (
                    <CategoryBadge key={category.id} category={category} size="md" />
                  ))}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Description */}
          {sponsor.description && (
            <div
              className="text-gray-700 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: sponsor.description }}
            />
          )}

          {/* Action Buttons */}
          {(sponsor.website || sponsor.email) && (
            <div className="flex flex-wrap gap-3">
              {sponsor.website && (
                <a
                  href={sponsor.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2 transition-colors"
                  onClick={onWebsiteClick}
                >
                  <Globe className="h-4 w-4" />
                  Visit Website
                </a>
              )}

              {sponsor.email && (
                <a
                  href={`mailto:${sponsor.email}`}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center gap-2 transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  Contact
                </a>
              )}
            </div>
          )}

          {/* Special Offers */}
          {showSpecialOffers && sponsor.specialOffers && sponsor.specialOffers.length > 0 && (
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">Special Offers for Attendees</h3>
              <ul className="space-y-1">
                {sponsor.specialOffers.map((offer, index) => (
                  <li key={index} className="flex items-start gap-2 text-blue-800">
                    <span className="text-blue-600 mt-0.5">•</span>
                    <span>{offer}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Materials */}
          {showMaterials && sponsor.materials && sponsor.materials.length > 0 && canDownloadMaterials && (
            <div>
              <h3 className="font-semibold mb-3">Resources & Materials</h3>
              <div className="space-y-2">
                {sponsor.materials.map(material => (
                  <a
                    key={material.id}
                    href={material.url}
                    className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors group"
                  >
                    <Download className="h-4 w-4 text-gray-500 group-hover:text-gray-700" />
                    <span className="flex-1">{material.name}</span>
                    <span className="text-xs text-gray-500 uppercase">{material.type}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Visit History */}
          {showLastVisited && lastVisitedAt && (
            <div className="text-sm text-gray-600 border-t pt-4">
              Last visited: {new Date(lastVisitedAt).toLocaleString()}
            </div>
          )}

          {/* If no description and no buttons shown above */}
          {!sponsor.description && !sponsor.website && !sponsor.email && (
            <div className="text-center">
              <p className="text-gray-500">No additional information available.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};